import React, { useState, useEffect, useCallback } from 'react';
import { FaPhone, FaVideo, FaPhoneSlash, FaArrowUp, FaArrowDown } from 'react-icons/fa';
import { callsAPI } from '../../utils/api';
import { getAvatarUrl } from '../../utils/avatar';
import { useCall } from '../../contexts/CallContext';

const formatDuration = (seconds) => {
  if (!seconds || seconds === 0) return '';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  if (m === 0) return `${s}s`;
  return `${m}m ${s}s`;
};

const formatCallTime = (dateString) => {
  const date = new Date(dateString);
  const now = new Date();
  const diff = now - date;
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const callDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());

  if (callDate.getTime() === today.getTime()) {
    return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  }
  if (diff < 7 * 86400000) {
    return date.toLocaleDateString([], { weekday: 'short' }) + ' ' + date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  }
  return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
};

const getStatusInfo = (call) => {
  const isMissed = call.status === 'missed' || call.status === 'declined';
  if (isMissed) {
    return {
      label: call.is_outgoing ? 'No answer' : 'Missed',
      color: 'text-red-500',
      icon: FaPhoneSlash,
    };
  }
  return {
    label: call.status === 'ended' ? formatDuration(call.duration) || 'Ended' : call.status,
    color: 'text-gray-500',
    icon: call.is_outgoing ? FaArrowUp : FaArrowDown,
  };
};

const CallHistoryList = () => {
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const { initiateCall, callState } = useCall();

  const loadHistory = useCallback(async () => {
    try {
      const res = await callsAPI.getHistory();
      setCalls(res.data);
    } catch (err) {
      // silently fail
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  const handleRedial = (call) => {
    if (callState !== 'idle') return;
    initiateCall(
      call.other_user.id,
      call.other_user.full_name,
      call.other_user.avatar_url,
      call.other_user.username,
      call.call_type
    );
  };

  if (loading) {
    return <div className="p-6 text-center text-gray-400 text-sm" data-testid="call-history-loading">Loading calls...</div>;
  }

  if (calls.length === 0) {
    return (
      <div className="p-6 text-center" data-testid="no-call-history">
        <div className="w-14 h-14 mx-auto mb-3 rounded-full bg-gray-100 flex items-center justify-center">
          <FaPhone className="text-gray-400" size={20} />
        </div>
        <p className="text-gray-500 font-medium">No calls yet</p>
        <p className="text-gray-400 text-sm mt-1">Start a call from any chat</p>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto" data-testid="call-history-list">
      {calls.map((call) => {
        const { label, color, icon: StatusIcon } = getStatusInfo(call);
        const isMissed = call.status === 'missed' || call.status === 'declined';

        return (
          <div
            key={call.id}
            className="p-3 flex items-center space-x-3 border-b border-gray-100 hover:bg-gray-50 transition-colors"
            data-testid={`call-record-${call.id}`}
          >
            <img
              src={getAvatarUrl(call.other_user?.avatar_url, call.other_user?.full_name)}
              alt={call.other_user?.full_name}
              className="w-12 h-12 rounded-full object-cover flex-shrink-0"
            />
            <div className="flex-1 min-w-0">
              <p className={`font-medium truncate ${isMissed && !call.is_outgoing ? 'text-red-600' : 'text-gray-900'}`}>
                {call.other_user?.full_name || 'Unknown'}
              </p>
              <div className="flex items-center space-x-1.5 mt-0.5">
                <StatusIcon size={10} className={color} />
                <span className={`text-xs ${color}`}>{label}</span>
                <span className="text-gray-300 text-xs">·</span>
                <span className="text-xs text-gray-400">{formatCallTime(call.created_at)}</span>
              </div>
            </div>
            <button
              onClick={() => handleRedial(call)}
              disabled={callState !== 'idle'}
              className="p-2.5 rounded-full hover:bg-gray-100 transition-colors text-[#4A90E2] disabled:opacity-40"
              data-testid={`redial-${call.id}`}
              title={`${call.call_type === 'video' ? 'Video' : 'Voice'} call`}
            >
              {call.call_type === 'video' ? <FaVideo size={16} /> : <FaPhone size={16} />}
            </button>
          </div>
        );
      })}
    </div>
  );
};

export default CallHistoryList;
